import { CustomModal } from "components";
import SignatureCanvas from "react-signature-canvas";
import { useRef, useState, createRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { addAttendanceInitialFormState, PAGE } from "./constants";
import { Button } from "reactstrap";
import apiService from "services/api";
import { useNotificationContext } from "contexts";
import ReactRoundedImage from "react-rounded-image";
import { useMutation } from "react-query";

export const AddAttendancePage = ({
	open,
	hide,
	event,
	setCurrPage,
	refetchSingleLessonRecord,
	refetchLessonRecords,
}) => {
	const { createNotification } = useNotificationContext();
	const teacherSignaturePadRef = useRef(null);
	const studentSignaturePadRef = useRef(
		event?.students
			?.filter((student) => !student.studentAttendanceRecorded)
			.map(() => createRef()),
	);
	const observerSignaturePadRef = useRef(
		event?.rawObservers
			?.filter((observer) => !observer.attendanceRecorded)
			.map(() => createRef()),
	);

	const { handleSubmit } = useForm({
		defaultValues: addAttendanceInitialFormState,
	});

	const addAttendance = () => {
		setError(false);
		const teacherSignature =
			teacherSignaturePadRef.current &&
			!teacherSignaturePadRef.current.isEmpty()
				? teacherSignaturePadRef.current.toDataURL()
				: null;
		const studentSignatures = studentSignaturePadRef.current
			?.filter((ref) => !ref.current.isEmpty())
			.map((ref) => {
				return {
					signature: ref.current.toDataURL(),
					studentId: ref.current.props.studentId,
				};
			});

		const observerSignatures = observerSignaturePadRef.current
			?.filter((ref) => !ref.current.isEmpty())
			.map((ref) => {
				return {
					signature: ref.current.toDataURL(),
					observerId: ref.current.props.observerId,
				};
			});

		if (
			!teacherSignature &&
			!studentSignatures.length &&
			!observerSignatures.length
		)
			return setError(true);

		return apiService.lessonRecord
			.addAttendance(event?.id, {
				...(teacherSignature && { teacherSignature }),
				studentSignatures,
				observerSignatures,
			})
			.then(() => {
				createNotification({
					type: "success",
					message: "Successfully added attendance",
				});
				setCurrPage(PAGE.VIEW_EVENT);
				refetchLessonRecords();
				refetchSingleLessonRecord();
			})
			.catch((err) => {
				createNotification({
					type: "danger",
					message: "Failed to add attendance: " + err.message,
				});
			});
	};

	const { isLoading, mutate: onSubmit } = useMutation(addAttendance);

	const [error, setError] = useState(false);

	const extendedHide = () => {
		setCurrPage(PAGE.VIEW_EVENT);
		hide();
	};
	return (
		<form onSubmit={handleSubmit(() => onSubmit())}>
			<CustomModal
				show={open}
				hideModal={extendedHide}
				title="Add Attendance"
				buttons={
					<Button color="success" type="submit" disabled={isLoading}>
						Submit
					</Button>
				}
				triggerAlertWhenClose={true}
				triggerAlertMessageWhenClose="Are you sure you want to close this tab?"
			>
				<div className="d-flex justify-content-center flex-wrap">
					{!event?.teacherSignaturePath && (
						<div className="mx-2 text-center">
							<div className="d-flex justify-content-center">
								<ReactRoundedImage
									image={"./genius-logo512.png"}
									roundedSize="1"
								/>
							</div>
							<label className="text-primary">
								Teacher {event?.teacher?.fullName} Signature
							</label>
							<div className="position-relative">
								<SignatureCanvas
									penColor="black"
									backgroundColor="#eeeeee"
									canvasProps={{
										width: 300,
										height: 250,
										className: "signature-pad",
									}}
									ref={teacherSignaturePadRef}
								/>
								<Button
									type="button"
									size="sm"
									style={{
										position: "absolute",
										top: 5,
										right: 5,
									}}
									onClick={() => teacherSignaturePadRef.current.clear()}
								>
									Clear
								</Button>
							</div>
						</div>
					)}
					{event?.rawObservers
						?.filter((observer) => !observer.attendanceRecorded)
						.map(
							(observer, index) =>
								!observer.signaturePath && (
									<div className="mx-2 text-center" key={index}>
										<div className="d-flex justify-content-center">
											<ReactRoundedImage
												image={"./genius-logo512.png"}
												roundedSize="1"
											/>
										</div>
										<label className="text-primary">
											Observer {observer?.observerDetail?.fullName}'s Signature
										</label>
										<div className="position-relative">
											<SignatureCanvas
												penColor="black"
												backgroundColor="#eeeeee"
												canvasProps={{
													width: 300,
													height: 250,
													className: "signature-pad",
												}}
												observerId={observer?.observerDetail?.id}
												ref={observerSignaturePadRef.current[index]}
											/>
											<Button
												type="button"
												size="sm"
												style={{
													position: "absolute",
													top: 5,
													right: 5,
												}}
												onClick={() =>
													observerSignaturePadRef.current[
														index
													]?.current.clear()
												}
											>
												Clear
											</Button>
										</div>
									</div>
								),
						)}
					{event?.students
						?.filter((student) => !student.studentAttendanceRecorded)
						.map(
							(student, index) =>
								!student.studentSignaturePath && (
									<div className="mx-2 text-center" key={index}>
										<div className="d-flex justify-content-center">
											<ReactRoundedImage
												image={student?.studentDetail?.nftPath}
												roundedSize="1"
											/>
										</div>
										<label className="text-primary">
											{student?.studentDetail?.firstName +
												" " +
												student?.studentDetail?.lastName}
											's Signature
										</label>
										<div className="position-relative">
											<SignatureCanvas
												penColor="black"
												backgroundColor="#eeeeee"
												canvasProps={{
													width: 300,
													height: 250,
													className: "signature-pad",
												}}
												studentId={student?.studentDetail?.id}
												ref={studentSignaturePadRef.current[index]}
											/>
											<Button
												type="button"
												size="sm"
												style={{
													position: "absolute",
													top: 5,
													right: 5,
												}}
												onClick={() =>
													studentSignaturePadRef.current[index]?.current.clear()
												}
											>
												Clear
											</Button>
										</div>
									</div>
								),
						)}
				</div>
				{error && (
					<div className="text-center text-danger">Signature is required</div>
				)}
			</CustomModal>
		</form>
	);
};
